import type { UIMessage } from 'ai';

import type { GetAssistantSessionsBySessionIdMessages200MessagesItem as ChatMessage } from '@/api/generated/lucioAPI.schemas';
import type { EmailDraft } from '@/utils/emailContentDisplayUtils';

const DRAFT_TOOL_NAME = 'draftEmail';
const REVISE_TOOL_NAME = 'reviseEmail';

type LooseDraft = Partial<EmailDraft>;

type ToolPart = {
  input?: unknown;
  state?: string;
  toolName?: string;
  type: string;
};

// `useChat` represents tool calls as either `{ type: 'tool-{name}' }` (when
// the tool is statically registered) or `{ type: 'dynamic-tool', toolName }`
// (when it isn't). We don't register tools with `useChat`, so all our tool
// parts arrive as 'dynamic-tool'. Match either form to be safe.
function isToolPart(part: unknown, toolName: string): part is ToolPart {
  if (typeof part !== 'object' || part === null) {
    return false;
  }
  const p = part as { toolName?: string; type?: string };
  if (p.type === `tool-${toolName}`) {
    return true;
  }
  if (p.type === 'dynamic-tool' && p.toolName === toolName) {
    return true;
  }
  return false;
}

function findToolPart(msg: UIMessage, toolName: string): ToolPart | undefined {
  for (const p of msg.parts) {
    if (isToolPart(p, toolName)) {
      return p;
    }
  }
  return undefined;
}

function asDraft(value: unknown): EmailDraft | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }
  const v = value as LooseDraft;
  if (!v.subject || !v.to || !v.body) {
    return null;
  }
  return {
    body: v.body,
    subject: v.subject,
    to: v.to,
    ...(v.cc && { cc: v.cc }),
    ...(v.bcc && { bcc: v.bcc }),
  };
}

function asPartialDraft(value: unknown): EmailDraft | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }
  const v = value as LooseDraft;
  return {
    body: v.body ?? '',
    subject: v.subject ?? '',
    to: v.to ?? '',
    ...(v.cc && { cc: v.cc }),
    ...(v.bcc && { bcc: v.bcc }),
  };
}

/**
 * Extract a finalized email draft from a server-stored chat message.
 * Returns null if the message is not a draft or content is unparseable.
 */
export function extractDraftFromServer(msg: ChatMessage): EmailDraft | null {
  if (msg.contentType !== 'email_draft' || !msg.content) {
    return null;
  }
  try {
    return asDraft(JSON.parse(msg.content));
  } catch {
    return null;
  }
}

/**
 * Extract revised + original drafts from a server-stored revision message.
 */
export function extractRevisionFromServer(
  msg: ChatMessage,
): { original: EmailDraft; revised: EmailDraft } | null {
  if (msg.contentType !== 'revision' || !msg.content) {
    return null;
  }
  try {
    const revised = asDraft(JSON.parse(msg.content));
    const original = msg.originalContent ? asDraft(JSON.parse(msg.originalContent)) : null;
    if (!revised || !original) {
      return null;
    }
    return { original, revised };
  } catch {
    return null;
  }
}

/**
 * Extract a (possibly partial) email draft from a streaming UI message's tool parts.
 * Handles both `input-streaming` (partial input) and `input-available` (final input) states.
 */
export function extractDraftFromLive(msg: UIMessage): EmailDraft | null {
  const part = findToolPart(msg, DRAFT_TOOL_NAME);
  if (!part) {
    return null;
  }
  return asPartialDraft(part.input);
}

export function extractRevisionFromLive(
  msg: UIMessage,
): { original: EmailDraft; revised: EmailDraft } | null {
  const part = findToolPart(msg, REVISE_TOOL_NAME);
  if (!part || typeof part.input !== 'object' || part.input === null) {
    return null;
  }
  const input = part.input as { original?: unknown; revised?: unknown };
  const original = asPartialDraft(input.original);
  const revised = asPartialDraft(input.revised);
  if (!original || !revised) {
    return null;
  }
  return { original, revised };
}

/**
 * Detect the response type of a streaming UI message based on its tool parts.
 */
export function detectLiveContentType(msg: UIMessage): 'email_draft' | 'revision' | 'text' {
  if (findToolPart(msg, DRAFT_TOOL_NAME)) {
    return 'email_draft';
  }
  if (findToolPart(msg, REVISE_TOOL_NAME)) {
    return 'revision';
  }
  return 'text';
}
